import { definePreset } from '@primeuix/themes'
// PrimeVue v5's default Aura preset assumes a 16px root font-size; this app's
// root font-size has always been the browser default (16px) but the UI was
// built against v4's component scale, which was calibrated for a 14px root.
// Use the compat preset to keep existing component sizing/spacing unchanged.
import Aura from '@primeuix/themes/aura-compat'
import * as Sentry from '@sentry/vue'
import { headSymbol } from '@unhead/vue'
import { createHead } from '@unhead/vue/client'
import { ToastService } from 'primevue'
import PrimeVue from 'primevue/config'
import type { App } from 'vue'

import { danger, info, primary, success, surface, warn } from './design-system/tokens'
import setupI18n from './i18n'
import router from './router'
import store from './stores/setup'

export default (app: App) => {
  console.info('Setting up app dependencies...')

  Sentry.init({
    app,
    enabled: import.meta.env.VITE_APP_ENV && import.meta.env.VITE_APP_ENV !== 'local',
    dsn: 'https://fc8034f60cc3a33ec9c74f0bc54f619a@o384990.ingest.us.sentry.io/4508761989971968',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.pinvision\.app/,
      /^https:\/\/api-dev\.pinvision\.app/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.VITE_APP_ENV || 'local',
    // Suppress deployment-artifact chunk errors (auto-handled by router.onError) and browser extension noise
    ignoreErrors: [
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'error loading dynamically imported module',
      "'text/html' is not a valid JavaScript MIME type",
      'Unable to preload CSS for',
      /Object Not Found Matching Id:\d+/,
      // Injected third-party/bot scripts calling nonexistent globals
      /^self\.\w+ is not a function$/,
    ],
    beforeSend(event) {
      const msg = event.exception?.values?.[0]?.value ?? ''
      if (msg.includes('Object Not Found Matching Id')) {
        return null
      }
      return event
    },
  })

  const head = createHead()
  app.provide(headSymbol, head)

  setupI18n(app)
  app.use(store)
  app.use(router)
  app.use(ToastService)

  const customPreset = definePreset(Aura, {
    // @primeuix/themes v3 dropped the top-level semantic.success/warn/danger
    // tokens; every component now references severity colors straight from
    // the primitive green/orange/red scales (e.g. button.success.background:
    // "{green.500}"). Overriding those primitive scales reproduces the same
    // brand alignment across all components without touching each one.
    primitive: {
      // Ramps come from src/design-system/tokens.ts, which tailwind.config.js
      // imports too — one definition, two consumers.
      green: success, // severity="success" → brand-green #5cc689
      sky: info, //     severity="info"    → info-blue #0ea5e9
      orange: warn, //  severity="warn"    → brand-orange #ffb13d
      red: danger, //   severity="danger"  → brand-red #ea4243
    },
    semantic: {
      primary, // brand ink #203036 — also the source of Tailwind's primary-*
      surface, // brand-hued neutrals, replacing Aura's slate — also Tailwind's surface-*
      // Aura paints text in {surface.700}; body copy is brand ink (the admin shell
      // overrides it to {surface.700} in layout/variables/_common.scss). Everything that
      // reads {text.color} (content, overlays, lists, nav) follows this one token.
      // Aura's muted/placeholder/float-label grey is {surface.500}: 4.2:1 on white,
      // below WCAG AA. {surface.600} is 6.7:1.
      text: { color: '{primary.500}', mutedColor: '{surface.600}' },
      formField: {
        borderRadius: '0.5rem',
        // The one Aura text token hardcoded to {surface.700} instead of {text.color}.
        color: '{text.color}',
        placeholderColor: '{surface.600}',
        floatLabelColor: '{surface.600}',
      },
    },
    components: {
      // Cards join panels on the 1rem surface tier and rest flat like every
      // other admin surface. Set through the preset rather than a `shadow-none!`
      // override in SCSS: `!important` on a rest-state property also killed
      // every consumer's `hover:` utility, which is how the dashboard shortcut
      // card and the dashboard KpiCards ended up with hover effects that never
      // painted. Aura's own default is a 12px radius plus a soft shadow.
      card: {
        root: {
          borderRadius: '1rem',
          shadow: 'none',
        },
      },
      // Buttons follow the same rule as cards: geometry lives in the preset,
      // not behind `!important` in SCSS. `_common.scss` used to force
      // `rounded-lg! font-semibold!` on .p-button and `rounded-full!` on
      // .p-button-rounded — the radius was already what Aura resolves from
      // formField.borderRadius above, and the other two are these two tokens.
      // Aura's `rounded` is 2rem, which stops short of a pill.
      button: {
        root: {
          roundedBorderRadius: '9999px',
          label: { fontWeight: '600' },
        },
      },
      // Admin sidebar geometry, ported from the hand-rolled `.admin-link` it replaced:
      // `flex gap-4 px-4 py-4 rounded-xl hover:bg-surface-100` on 24px icons that
      // inherit the link's colour, and `pl-8 py-3` on the nested items. The defaults
      // are a denser 2.25rem row with 1rem grey icons, so every value is spelled out.
      sidebar: {
        // Content area picks up the marketing hero's faded backdrop
        // (.bg-gradient-hero in styles/components/_site.scss). Set through the
        // token rather than a class on SidebarMain, because .p-sidebar-main
        // declares its own `background` at equal specificity.
        main: { background: 'linear-gradient(to top right, {primary.50}, #fff)' },
        header: { padding: '1rem', gap: '1rem' },
        footer: { padding: '1rem', gap: '0.5rem' },
        content: { gap: '0.25rem' },
        group: { padding: '1rem' },
        menu: { gap: '0.25rem' },
        menuButton: {
          padding: '1rem',
          gap: '1rem',
          height: '3.5rem',
          borderRadius: '0.75rem',
          iconOnlyWidth: '3.5rem',
          focusBackground: '{surface.100}',
          activeBackground: '{primary.500}',
          activeColor: '{primary.contrast.color}',
          icon: { size: '1.5rem', color: 'inherit', focusColor: 'inherit' },
        },
        menuSub: {
          gap: '0.25rem',
          collapsibleIndent: '0',
          collapsibleTopMargin: '0.25rem',
        },
        menuSubButton: {
          padding: '0.75rem 1rem 0.75rem 2rem',
          gap: '1rem',
          height: '3rem',
          borderRadius: '0.75rem',
          focusBackground: '{surface.100}',
          activeBackground: '{primary.500}',
          activeColor: '{primary.contrast.color}',
          icon: { size: '1.5rem', color: 'inherit', focusColor: 'inherit' },
        },
      },
    },
  })

  app.use(PrimeVue, {
    // PrimeVue 5 is commercially licensed: without a key it renders a license
    // notice over the UI. Key is verified offline, so it is safe in the bundle.
    license: import.meta.env.VITE_PRIMEVUE_LICENSE,
    theme: {
      preset: customPreset,
      options: {
        prefix: 'p',
        darkModeSelector: 'light',
        cssLayer: false,
      },
    },
  })
}
